import { snakeCase, camelCase, escape } from 'es-toolkit/compat';
import { useState, useEffect } from 'react';
import { CommandProps, createNodeFromContent, Extension } from '@tiptap/core';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Autocomplete,
  Paper,
  Theme,
  Typography,
  useTheme,
  Box,
} from '@mui/material';
import { usePostHog } from 'posthog-js/react';

export type PlaceholderValue =
  | string
  | {
      name: string;
      type?: 'select';
      options?: string[];
    };

export type RichTextSnippet = {
  name: string;
  category?: string;
  template: string;
  placeholders: Record<string, PlaceholderValue>; // key: variable, value: human-friendly name or config object
};

export type Placeholder = {
  name: string; // human-friendly name
  variable: string; // variable name used in template, e.g. "firstName"
  required?: boolean; // if true, must be filled before inserting snippet
  type?: 'select'; // field type
  options?: string[]; // if provided, user can select from these options
};

export type Template = {
  templateStr: string;
  placeholders: Placeholder[];
};

export type SnippetDialogProps = {
  snippets: RichTextSnippet[];
  open: boolean;
  onClose: () => void;
  onConfirm: (template: Template, values: Record<string, string>) => void;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    insertSnippet: {
      insertSnippet(
        template: Template,
        values: Record<string, string>,
      ): ReturnType;
    };
  }
}

export const SnippetExtension = Extension.create({
  name: 'snippet',
  addCommands: () => {
    return {
      insertSnippet:
        (template: Template, values: Record<string, string>) =>
        ({ commands, editor }: CommandProps) => {
          // Replace placeholders with escaped user values
          const text = template.placeholders.reduce((acc, ph) => {
            const phVar = camelCase(ph.variable);
            const val = values[phVar];
            const displayValue = val && val.trim() ? val : `[${ph.name}]`;
            // Escape HTML in user-provided values to prevent injection
            const escapedValue = escape(displayValue);

            return acc.replace(
              new RegExp(
                `{{${phVar}}}|{{${snakeCase(phVar)}}}|{{${camelCase(phVar)}}}`,
                'g',
              ),
              escapedValue,
            );
          }, template.templateStr);

          // insertContent inserts a text-only string verbatim, so escaped values show as literal entities.
          // Parse first and insert the resulting JSON to avoid things like & showing as &amp;
          return commands.insertContent(
            createNodeFromContent(text, editor.schema, {
              parseOptions: { preserveWhitespace: 'full' },
            }).toJSON(),
          );
        },
    };
  },
});

const extractPlaceholders = (templateStr: string): string[] => {
  const platcholders = templateStr
    .split(/({{\w+}})/g)
    .filter((part) => Boolean(part.match(/^{{(\w+)}}$/)))
    .map(camelCase);
  return [...new Set(platcholders)];
};

const getTemplatesForSnippet = (snippet: RichTextSnippet): Template[] => {
  const variations = snippet.template.split('\n');
  const requiredSet = extractPlaceholders(variations[0]);

  return variations.map((templateStr) => {
    const placeholders = extractPlaceholders(templateStr);
    return {
      templateStr,
      placeholders: placeholders.map((ph) => {
        const placeholderConfig = snippet.placeholders[ph];
        const isObject = typeof placeholderConfig === 'object';

        return {
          variable: ph,
          name: isObject ? placeholderConfig.name : placeholderConfig || ph,
          required: requiredSet.includes(ph),
          type: isObject ? placeholderConfig.type : undefined,
          options: isObject ? placeholderConfig.options : undefined,
        };
      }),
    };
  });
};

const getMatchingTemplateForPopulatedValues = (
  snippet: RichTextSnippet | null,
  values: Record<string, string>,
): Template | undefined => {
  if (!snippet || !snippet.template) return undefined;
  const templates = getTemplatesForSnippet(snippet);

  return templates.reduce((acc, curr) => {
    const accCount = acc.placeholders.filter(
      (ph) => values[ph.variable] && values[ph.variable].trim(),
    ).length;
    const currCount = curr.placeholders.filter(
      (ph) => values[ph.variable] && values[ph.variable].trim(),
    ).length;

    return currCount > accCount ? curr : acc;
  }, templates[0]);
};

const renderSnippetPreview = (
  template: Template,
  values: Record<string, string>,
  theme: Theme,
): JSX.Element => {
  const placeholderMap = Object.fromEntries(
    template.placeholders.map((ph) => [ph.variable, ph.name]),
  );

  const placeholderBackground = theme.palette.brand[500];
  const placeholderColor =
    theme.palette.mode === 'dark'
      ? theme.palette.primary.contrastText
      : theme.palette.grey[900];

  // Replace placeholders with their values (or default text)
  let html = template.templateStr;
  template.placeholders.forEach((ph) => {
    const val = values[ph.variable];
    const displayValue =
      val && val.trim() ? val : `[${placeholderMap[ph.variable]}]`;
    // Escape HTML in the display value to prevent injection
    const escapedValue = escape(displayValue);

    const styledPlaceholder = `<span style="word-break: break-word; color: ${placeholderColor}; background: ${placeholderBackground}; padding: 0 2px; border-radius: 2px; white-space: pre-wrap;">${escapedValue}</span>`;

    // Replace all variations of the placeholder
    const phVar = camelCase(ph.variable);
    html = html.replace(
      new RegExp(
        `{{${phVar}}}|{{${snakeCase(phVar)}}}|{{${camelCase(phVar)}}}`,
        'g',
      ),
      styledPlaceholder,
    );
  });

  return (
    <Box
      sx={{
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        // Styling to mimic the inline info card from inlineInfoCardExtension.tsx
        '& div[data-tiptap-render="inline-info-card"]': {
          padding: 1,
          margin: '8px 0',
          backgroundColor: 'background.inlineInfoCard',
          borderRadius: '8px',
          '& p': {
            fontSize: 'subtitle2.fontSize',
            margin: 0,
            padding: '0 !important',
          },
        },
      }}
      // Using dangerouslySetInnerHTML to render the HTML snippet with styles
      // Considered safe because template HTML is trusted and user values are escaped
      // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const getOrderedPlaceholders = (snippet: RichTextSnippet): Placeholder[] => {
  const templates = getTemplatesForSnippet(snippet);

  return Array.from(
    templates
      .map((t) => t.placeholders)
      .flat()
      .reduce(
        (map, ph) => (map.has(ph.variable) ? map : map.set(ph.variable, ph)),
        new Map<string, Placeholder>(),
      )
      .values(),
  );
};

export const SnippetDialog = ({
  snippets,
  open,
  onClose,
  onConfirm,
}: SnippetDialogProps): JSX.Element | null => {
  const { t } = useTranslation();
  const [selectedSnippet, setSelectedSnippet] =
    useState<RichTextSnippet | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const theme = useTheme();
  const posthog = usePostHog();

  // When snippets load or dialog opens, reset selectedSnippet and values
  useEffect(() => {
    if (open && snippets.length > 0) {
      setSelectedSnippet(null);
      setValues({});
    }
  }, [open, snippets]);

  if (!open) return null;

  const template = getMatchingTemplateForPopulatedValues(
    selectedSnippet,
    values,
  );

  const allPlaceholdersFilled = (): boolean => {
    if (!template) return false;

    const placeholders = template.placeholders.map((ph) => ph.variable);
    if (placeholders.length === 0) return true;

    return placeholders.every((ph) => values[ph]?.trim());
  };

  const handleSubmit = (): void => {
    if (!template) return;
    if (!selectedSnippet || !allPlaceholdersFilled()) return;
    posthog.capture('clicked_rich_text_snippet_insert', {
      snippetName: selectedSnippet.name,
      snippetCategory: selectedSnippet.category || 'General',
    });
    onConfirm(template, values);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('richTextEditor.addSnippet')}</DialogTitle>
      <DialogContent>
        <Autocomplete
          options={snippets}
          groupBy={(option) => option.category || 'General'}
          getOptionLabel={(option) => option.name}
          value={selectedSnippet}
          onChange={(_, newValue) => {
            if (newValue) {
              setSelectedSnippet(newValue);
              setValues(
                Object.fromEntries(
                  Object.keys(newValue.placeholders).map((ph) => [ph, '']),
                ),
              );
            }
          }}
          autoHighlight
          openOnFocus
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('richTextEditor.snippetSelect')}
              fullWidth
              margin="normal"
              autoFocus
            />
          )}
        />
        {selectedSnippet &&
          Object.keys(selectedSnippet.placeholders).length > 0 && (
            <>
              {Array.from(getOrderedPlaceholders(selectedSnippet)).map((ph) => {
                // Render dropdown for select type, otherwise render text field
                if (
                  ph.type === 'select' &&
                  ph.options &&
                  ph.options.length > 0
                ) {
                  return (
                    <Autocomplete
                      key={ph.variable}
                      options={ph.options}
                      value={values[ph.variable] || ''}
                      onChange={(_, newValue) => {
                        setValues({ ...values, [ph.variable]: newValue || '' });
                      }}
                      onInputChange={(_, newInputValue) => {
                        setValues({ ...values, [ph.variable]: newInputValue });
                      }}
                      freeSolo
                      fullWidth
                      openOnFocus
                      autoHighlight
                      forcePopupIcon
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={ph.name}
                          placeholder={ph.name}
                          required={ph.required}
                          variant="outlined"
                          margin="normal"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSubmit();
                            }
                          }}
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: params.InputProps.endAdornment,
                            },
                          }}
                        />
                      )}
                    />
                  );
                }

                return (
                  <TextField
                    key={ph.variable}
                    label={ph.name}
                    value={values[ph.variable] || ''}
                    placeholder={ph.name}
                    required={ph.required}
                    variant="outlined"
                    onChange={(e) =>
                      setValues({ ...values, [ph.variable]: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSubmit();
                      }
                    }}
                    fullWidth
                    margin="normal"
                  />
                );
              })}
            </>
          )}
        {selectedSnippet && (
          <>
            {/* Preview Section */}
            <Typography
              variant="body1"
              component="h3"
              marginY={2}
              fontWeight="bold"
            >
              <b>{t('richTextEditor.snippetPreview')}:</b>
            </Typography>
            <Paper
              elevation={0}
              sx={{
                padding: 2,
                backgroundColor: theme.palette.background.inlineInfoCard,
              }}
              data-testid="snippet-preview"
            >
              {template ? renderSnippetPreview(template, values, theme) : null}
            </Paper>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="neutral">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          disabled={!allPlaceholdersFilled()}
        >
          {t('richTextEditor.snippetDone')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
